const _windowLocale = () => (typeof window !== 'undefined' && window.locale) || 'en';
const _isFr = () => _windowLocale().toLowerCase().startsWith('fr');
const _windowCurrency = () => (typeof window !== 'undefined' && window.currency) || 'USD';
const _currencyLocale = () => {
    const cur = _windowCurrency();
    if (cur === 'GBP') return 'en-GB';
    return _isFr() ? 'fr-CA' : 'en-CA';
};

// Lazy-initialized singletons — created once per session, not per call.
let _dateFormatter = null;
let _currencyFormatter = null;
let _numberFormatter = null;

export const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    if (!_dateFormatter) {
        _dateFormatter = new Intl.DateTimeFormat(_isFr() ? 'fr-CA' : 'en-CA', {
            year: 'numeric', month: 'short', day: 'numeric',
        });
    }
    return _dateFormatter.format(new Date(dateString));
};

/**
 * Format a numeric value as currency
 * @param {number} amount - The amount to format
 * @param {string} locale - The locale to use for formatting (auto-detected when omitted)
 * @param {string} currency - The currency code (default: 'CAD')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, locale = null, currency = null) => {
    if (amount === null || amount === undefined || isNaN(amount) || !isFinite(amount)) return '-';

    const effectiveLocale = locale || _currencyLocale();
    const effectiveCurrency = currency || _windowCurrency();
    return new Intl.NumberFormat(effectiveLocale, {
        style: 'currency',
        currency: effectiveCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
};

export const formatNumber = (number) => {
    if (number === null || number === undefined) return 'N/A';
    if (!_numberFormatter) {
        _numberFormatter = new Intl.NumberFormat(_isFr() ? 'fr-CA' : 'en-CA');
    }
    return _numberFormatter.format(number);
};

/**
 * Format a percentage
 * @param {number} value - The decimal value to format as percentage
 * @param {number} decimalPlaces - Number of decimal places (default: 0)
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, decimalPlaces = 0) => {
    if (value === null || value === undefined) return 'N/A';

    const pct = (value * 100).toFixed(decimalPlaces);
    return _isFr() ? `${pct}\u00a0%` : `${pct}%`;
};

/**
 * Returns the locale-appropriate percent symbol.
 * French typography requires a non-breaking space before %: "80 %"
 * @returns {string} '\u00a0%' for French, '%' otherwise
 */
export const pctSymbol = () => _isFr() ? '\u00a0%' : '%';

/**
 * Returns the currency symbol for the current company.
 * Reads from window.currency (set by CurrencyProvider/railsContext).
 * @returns {string} '$' for USD/CAD, 'GBP' for GBP
 */
export const currencySymbol = () => {
    const cur = _windowCurrency();
    if (cur === 'GBP') return '\u00a3';
    return '$';
};

/**
 * Truncate a string to a specific length and add ellipsis
 * @param {string} text - The text to truncate
 * @param {number} maxLength - The maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 100) => {
    if (!text) return '';

    if (text.length <= maxLength) return text;

    return `${text.substring(0, maxLength)}...`;
};

/**
 * Format a file size
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Format a phone number to a standard format (XXX) XXX-XXXX
 * @param {string} phoneNumber - The phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';

    // Strip all non-numeric characters
    const cleaned = phoneNumber.replace(/\D/g, '');

    // Format based on length
    if (cleaned.length === 10) {
        return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6, 10)}`;
    }

    return phoneNumber;
};

/**
 * Capitalize the first letter of each word in a string
 * @param {string} text - The text to capitalize
 * @returns {string} Capitalized text
 */
export const capitalizeWords = (text) => {
    if (!text) return '';

    return text
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

/**
 * Convert a snake_case string to Title Case
 * @param {string} text - The snake_case text
 * @returns {string} Title Case text
 */
export const snakeToTitleCase = (text) => {
    if (!text) return '';

    return text
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};